import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
	AwardRibbonOutline,
	Box,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	DocumentReportOutline,
	HistoryOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	MenuOutline,
	Portal,
	rem,
	SmileyFaceOutline,
	TargetOutline,
	Text,
	UserGroupOutline,
	UsersOutline,
	VStack,
} from '@bonusly/design-system';
import { Permission, ProtectedContent, useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { useLeaderboardPanel } from '../../hooks/use-navbar-panels';
import { useCurrentUserReports } from '../../modules/team-management/hooks/use-current-user-reports';
import { LeaderboardPanel } from '../../modules/user-navbar/components/leaderboard';
import { TeamPanel } from '../../modules/user-navbar/components/team/team-panel';
import { Conceal } from '../display/conceal';
import { getStyle, NAV_ITEM_WIDTH } from './global-mobile-bottom-nav';

interface MoreActionMenuProps {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
}

// Check if path is active (same logic as UserNavLink)
const isPathActive = (linkPath: string, currentPath: string): boolean => {
	return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
};

type MoreMenuNavLinkProps = {
	to: string;
	icon: ReactElement;
	label: string;
	onClose: () => void;
	currentPath: string;
};

const MoreMenuNavLink: FC<MoreMenuNavLinkProps> = ({ to, icon, label, onClose, currentPath }) => {
	const isActive = isPathActive(to, currentPath);
	const styles = getStyle(isActive);

	return (
		<MenuItem
			as={Link}
			to={to}
			onClick={onClose}
			px={rem(12)}
			py={rem(10)}
			borderRadius="md"
			_hover={{ bg: 'secondary.bgAlpha' }}
			_focus={{ bg: 'secondary.bgAlpha' }}
			{...styles}
		>
			<Box display="flex" alignItems="center" gap={rem(12)}>
				{icon}
				<Text fontSize={100} fontWeight="medium">
					{label}
				</Text>
			</Box>
		</MenuItem>
	);
};

export const MoreActionMenu: FC<MoreActionMenuProps> = ({ isOpen, onOpen, onClose }) => {
	const { t } = useTranslation('navigation');
	const location = useLocation();
	const currentPath = location.pathname;
	const user = useCurrentUser();
	const { hide_competitive_features: hideCompetitiveFeatures } = useCurrentCompany();
	const hideLeaderboard = hideCompetitiveFeatures && !user.global_admin;

	// Team Panel state
	const [isTeamPanelOpen, setIsTeamPanelOpen] = useState(false);
	const { data: directReports = [] } = useCurrentUserReports();

	// Leaderboard Panel state
	const { isOpen: isLeaderboardOpen, open: openLeaderboard, close: closeLeaderboard } = useLeaderboardPanel();

	const menuItemStyles = {
		bg: 'bg.surface',
		px: rem(12),
		py: rem(10),
		borderRadius: 'md',
		_hover: { bg: 'secondary.bgAlpha' },
		_focus: { bg: 'secondary.bgAlpha' },
	};

	const handleViewTeam = () => {
		onClose();
		setIsTeamPanelOpen(true);
	};

	const handleCloseTeamPanel = () => {
		setIsTeamPanelOpen(false);
	};

	const handleOpenLeaderboard = () => {
		onClose();
		openLeaderboard();
	};

	return (
		<Menu placement="top" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
			<MenuButton as={Box} gap={rem(4)} aria-label={t('mobileBottomNav.more')} alignItems="center" textAlign="center" w={rem(NAV_ITEM_WIDTH)} cursor="pointer">
				<MenuOutline size="xl" color="secondary.fg" />
				<Text fontSize="xs" color="secondary.fg" fontWeight="semibold">
					{t('mobileBottomNav.more')}
				</Text>
			</MenuButton>

			<Portal>
				<MenuList
					py={rem(8)}
					px={rem(8)}
					bgColor="bg.surface"
					borderRadius="lg"
					borderColor="border.default"
					borderWidth="1px"
					shadow="lg"
					minW={rem(200)}
					zIndex="popover"
					mr={rem(16)}
				>
					<VStack spacing={rem(2)} align="stretch">
						{/* 1:1s */}
						<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
							<MoreMenuNavLink
								to="/meet"
								icon={<ChatAlt2Outline size="md" />}
								label={t('mobileBottomNav.moreMenu.oneOnOnes')}
								onClose={onClose}
								currentPath={currentPath}
							/>
						</ProtectedContent>

						{/* GOALS */}
						<ProtectedContent permissions={Permission.CanAccessGoals}>
							<MoreMenuNavLink
								to="/growth/goals"
								icon={<TargetOutline size="md" />}
								label={t('mobileBottomNav.moreMenu.goals')}
								onClose={onClose}
								currentPath={currentPath}
							/>
						</ProtectedContent>

						{/* RECAPS */}
						<ProtectedContent permissions={Permission.CanAccessRecaps}>
							<MoreMenuNavLink
								to="/growth/recap-review"
								icon={<HistoryOutline size="md" />}
								label={t('mobileBottomNav.moreMenu.recaps')}
								onClose={onClose}
								currentPath={currentPath}
							/>
						</ProtectedContent>

						{/* ASK FOR ADVICE (FEEDBACK) */}
						<ProtectedContent permissions={Permission.CanAccessAdvice}>
							<MoreMenuNavLink
								to="/growth/feedback"
								icon={<ChatAltOutline size="md" />}
								label={t('mobileBottomNav.moreMenu.feedback')}
								onClose={onClose}
								currentPath={currentPath}
							/>
						</ProtectedContent>

						{/* INCENTIVES */}
						<ProtectedContent permissions={Permission.CanAccessIncentivesExperience}>
							<MoreMenuNavLink
								to="/incentives"
								icon={<AwardRibbonOutline size="md" />}
								label={t('links.incentives')}
								onClose={onClose}
								currentPath={currentPath}
							/>
						</ProtectedContent>

						{/* LEADERBOARD */}
						<ProtectedContent permissions={Permission.CanAccessAnalytics}>
							<Conceal if={hideLeaderboard}>
								<MenuItem onClick={handleOpenLeaderboard} {...menuItemStyles}>
									<Box display="flex" alignItems="center" gap={rem(12)}>
										<ChartBarOutline size="md" />
										<Text fontSize={100} fontWeight="medium">
											{t('links.leaderboard')}
										</Text>
									</Box>
								</MenuItem>
							</Conceal>
						</ProtectedContent>

						<ProtectedContent permissions={Permission.CanAccessTeamHub}>
							{/* YOUR TEAM */}
							<MoreMenuNavLink
								to="/team/your-team"
								icon={<UsersOutline size="md" />}
								label={t('mobileBottomNav.moreMenu.yourTeam')}
								onClose={onClose}
								currentPath={currentPath}
							/>

							{/* PULSE RESULTS */}
							<ProtectedContent permissions={Permission.CanAccessCheckIn}>
								<MoreMenuNavLink
									to="/team/pulse-results"
									icon={<SmileyFaceOutline size="md" />}
									label={t('mobileBottomNav.moreMenu.pulseResults')}
									onClose={onClose}
									currentPath={currentPath}
								/>
							</ProtectedContent>

							{/* TEAM REPORT */}
							<MoreMenuNavLink
								to="/team/team-report"
								icon={<DocumentReportOutline size="md" />}
								label={t('mobileBottomNav.moreMenu.teamReport')}
								onClose={onClose}
								currentPath={currentPath}
							/>

							{/* VIEW TEAM */}
							{directReports.length > 0 && (
								<MenuItem onClick={handleViewTeam} {...menuItemStyles}>
									<Box display="flex" alignItems="center" gap={rem(12)}>
										<UserGroupOutline size="md" />
										<Text fontSize={100} fontWeight="medium">
											{t('mobileBottomNav.moreMenu.viewTeam')}
										</Text>
									</Box>
								</MenuItem>
							)}
						</ProtectedContent>
					</VStack>
				</MenuList>
			</Portal>

			{/* Team Panel */}
			<TeamPanel isOpen={isTeamPanelOpen} onClose={handleCloseTeamPanel} isSidebarExpanded={false} directReports={directReports} />

			{/* Leaderboard Panel */}
			<LeaderboardPanel isOpen={isLeaderboardOpen} onClose={closeLeaderboard} isSidebarExpanded={false} />
		</Menu>
	);
};
